/// <reference types="new-relic-browser" />
import {
  isDevelopment,
  isProduction,
  isStageTwo,
  isStaging,
  isEndToEndTest,
} from 'utils/environment';
import '../utils/monkeyPatchConsole';
import React, { useEffect, useState } from 'react';
import App from 'next/app';
import configJSON from 'config.json';
import { useRouter } from 'next/router';
import { appWithTranslation, i18n } from 'next-i18next';
import { LocaleManager } from 'utils/locale/locale-manager';
import HeadData from 'components/head-data';
import LocationPickerScreen from 'components/location-picker-screen';
import UserContextProvider from 'contexts/user';
import SavedListingContextProvider from 'contexts/saved-listing';
import ModalContextProvider from 'contexts/modal';
import ThemeContextProvider from 'contexts/theme';
import { trackAppLoadedEvent, trackWebVitals } from 'utils/google-tag-manager';
import PreferencesContextProvider, { LastSearchLocation } from 'contexts/preferences';
import { extractListingParamsData } from 'contexts/preferences/listing-params/utils';
import { extractSchoolParamsData } from 'contexts/preferences/school/utils';
import FeaturesContextProvider, { FeaturesType } from 'contexts/features';
import ChatContextProvider from 'contexts/chat';
import SnackbarContextProvider from 'contexts/snackbar';
import { GrowthBookProvider } from 'contexts/growthbook/GrowthBookProvider';
import isUserAgentCrawler, { isUserAgentLighthouse } from 'utils/crawler-agent';
import 'styles/app.scss';
import { getFeaturesOverrideFromRequest } from 'utils/features';
import storage from 'utils/storage';
import AccessibilityWidget from 'components/accessibility-widget';
import { TrustArcCookieData, isTrustarcCookieTypeAllowed } from 'utils/trustarc-cookies';
import {
  getLastSearchLocationTag,
  SCREEN_COOKIE_NAME,
  USER_COOKIE_NAME,
  LOC_PICKER_COOKIE_NAME,
  CONNECTION_REQUEST_DISCLAIMERS_COOKIE_NAME,
  NOTICE_GDPR_PREFS_COOKIE_NAME,
  DEFAULT_EXPIRE_DAYS,
  USER_LOCATION_COOKIE_NAME,
  MAINTENANCE_BANNER_DISMISSED_COOKIE_NAME,
  FEEDBACK_BANNER_DISMISSED_COOKIE_NAME,
} from 'constants/cookies';
import {
  GTM_SCRIPT,
  HUBSPOT_TRACKING_SCRIPT,
  ZOOCASA_MAKE_IT_RAIN_GTM_SCRIPT_SRC,
  ZOOCASA_MAKE_IT_RAIN_GTM_SCRIPT_CONFIG,
  TRUSTARC_DIV_ID,
  ACTIVECAMPAIGN_SCRIPT,
} from 'constants/HTMLAttributes';
import Cookies from 'js-cookie';
import {
  getObjectFromRequestCookie,
  getStringFromRequestCookie,
  getPopularCitiesCookie,
} from 'utils/cookies';

import dynamic from 'next/dynamic';
import { buildClassName } from 'utils/build-class-name';
import { SET_COOKIE_HEADER_NAME, USER_AGENT_HEADER_NAME, X_ZOOCASA_WEBVIEW_HEADER_NAME } from 'constants/headers';
import { getSiteLocationFromRequest } from 'utils/site-location';
import { getUserLocationFromServerSideRequest, IUserLocation } from 'utils/user-location';
import MiddlewareManager from 'utils/fetchWithRetry';
import { useIsDesktop } from 'hooks/use-size-class';
import cookie from 'cookie';
import { CountryCodeList, type CountryCode } from 'types/countries';
import { createTenantHeaderMiddleware } from '@tenants/utils/tenant-injector-middleware';
import { getOrCreateDeviceId } from 'utils/device-id';
import { isClientSide } from 'utils/host-config';
import {
  HydrationBoundary,
  QueryClient,
  QueryClientProvider,
} from '@tanstack/react-query';
import { shouldShowAccessibilityWidget } from 'utils/accessibility';
import { getTenantById, getTenantFromRequestHeader, isTenantExpCA, isZoocasaTenant, TenantId, isTenantExpCommercial } from '@tenants';
import { GtmScript } from 'components/gtm-script';
import { MAINTENANCE_BANNER_FLAG, FEEDBACK_BANNER_FLAG } from 'constants/feature-flags';
import { DISMISSAL_DURATIONS } from 'components/banner/constants/dismissalDurations';
import { Build as MaintenanceIcon } from '@mui/icons-material';
import { ThemeProvider as MUIThemeProvider, createTheme } from '@mui/material/styles';
import { ZoocasaScript } from 'components/scripts/zoocasa-script';
import { ExpScript } from 'components/scripts/exp-script';
import { FEEDBACK_BANNER_SURVEY_URL } from 'constants/urls';
import { GTM_FEEDBACK_BANNER_SURVEY_CLICK, GTM_FEEDBACK_BANNER_CLOSE } from 'constants/events';
import NoteIcon from 'components/icon/note-icon';
import PageBanner from 'components/page-banner';
import Script from 'next/script';

import type {
  AppProps,
  AppContext,
  NextWebVitalsMetric,
  AppInitialProps,
} from 'next/app';

import type { SearchSuggestions } from 'components/suggested-location-dropdown';
import type { ListingParams } from 'contexts/preferences/listing-params/types';
import type { SchoolParams } from 'contexts/preferences/school/types';
import type { IUserContext, User } from 'contexts/user';
import type { Screen } from 'utils/types';

const parseFeatures = (tenantId: TenantId | null, features: FeaturesType ) => isTenantExpCA(tenantId) ? { ...features, useUsListings: false }: features;

ZoocasaNextApp.getInitialProps = async (context: AppContext): Promise<ZoocasaNextInitialProps> => {
  const { ctx: { req: request, res }} = context;

  const isCrawler = isUserAgentCrawler(request?.headers);
  const isCrawlerLighthouse = isUserAgentLighthouse(request?.headers?.[USER_AGENT_HEADER_NAME]);
  const isMobile = !!request?.headers?.[USER_AGENT_HEADER_NAME]?.toLowerCase().includes('mobile');

  const featureOverrides = getFeaturesOverrideFromRequest(request);
  const features = { ...configJSON.features, ...featureOverrides };
  const tenant = getTenantFromRequestHeader(request, configJSON.defaultTenantId as TenantId);

  const cookiePreference: string|undefined = getStringFromRequestCookie(NOTICE_GDPR_PREFS_COOKIE_NAME, request);
  const functionalCookiePreference: TrustArcCookieData = { cookiePreference, type: 'functional' };
  const advertisingCookiePreference: TrustArcCookieData = { cookiePreference, type: 'advertising' };
  const functionalCookiesEnabled = isTrustarcCookieTypeAllowed(functionalCookiePreference);
  const isBlogWidget = context.router.asPath?.startsWith('/team/');

  let userLocation: IUserLocation | undefined;
  if (functionalCookiesEnabled) {
    userLocation = await getUserLocationFromServerSideRequest(request);
    if (userLocation) {
      res?.setHeader(SET_COOKIE_HEADER_NAME, cookie.serialize(USER_LOCATION_COOKIE_NAME, JSON.stringify(userLocation), {
        httpOnly: false,
        secure: !isDevelopment,
        maxAge: DEFAULT_EXPIRE_DAYS,
      }));
    }
  }
  const siteLocation = getSiteLocationFromRequest(request, userLocation?.countryCode);

  const hasShownSitePickerScreen = getStringFromRequestCookie(LOC_PICKER_COOKIE_NAME, request) === 'true';
  const storedConnRequestDisclaimers = getStringFromRequestCookie(CONNECTION_REQUEST_DISCLAIMERS_COOKIE_NAME, request) === 'true';

  const deviceId = getOrCreateDeviceId(request, res);

  const userPopularCities = getPopularCitiesCookie(request, userLocation?.slug);
  const lastSearchLocation = getObjectFromRequestCookie<LastSearchLocation>(getLastSearchLocationTag(tenant.theme.id), request);
  return {
    ...await App.getInitialProps(context),
    listingParamsData: extractListingParamsData(request),
    schoolParamsData: extractSchoolParamsData(request),
    screen: getObjectFromRequestCookie<Screen>(SCREEN_COOKIE_NAME, request),
    user: getObjectFromRequestCookie<User>(USER_COOKIE_NAME, request) || null,
    storedUserLocation: userLocation,
    storedSiteLocation: siteLocation,
    hasShownSitePickerScreen,
    storedConnRequestDisclaimers,
    features: parseFeatures(tenant.id, features),
    tenantId: tenant.id,
    isCrawler,
    isCrawlerLighthouse,
    isWebView: !!request?.headers[X_ZOOCASA_WEBVIEW_HEADER_NAME],
    functionalCookiesEnabled,
    advertisingCookiesEnabled: isTrustarcCookieTypeAllowed(advertisingCookiePreference),
    lastSearchLocationCookie: lastSearchLocation,
    storedUserPopularCities: userPopularCities,
    deviceId,
    isMobile,
    isBlogWidget,
  };
};

type BaseProps = {
  listingParamsData: ListingParams;
  schoolParamsData: SchoolParams;
  screen?: Screen;
  user: IUserContext['user'];
  storedUserLocation?: IUserLocation;
  storedSiteLocation?: CountryCode;
  hasShownSitePickerScreen: boolean;
  storedConnRequestDisclaimers: boolean;
  features: FeaturesType;
  tenantId?: TenantId;
  isCrawler: boolean;
  isCrawlerLighthouse: boolean;
  isWebView: boolean;
  functionalCookiesEnabled: boolean;
  advertisingCookiesEnabled: boolean;
  lastSearchLocationCookie?: LastSearchLocation;
  storedUserPopularCities?: SearchSuggestions[];
  deviceId?: string;
  isMobile: boolean;
  isBlogWidget: boolean;
};

type ZoocasaNextInitialProps = AppInitialProps & BaseProps;
type ZoocasaNextProps = AppProps & BaseProps;

function sendToGA(metric: NextWebVitalsMetric) {
  trackWebVitals('web-vitals-' + metric.name, metric.value);
  if (process.env.TEST_ENVIRONMENT) {
    console.log(`${metric.name}: ${metric.value}`);
  }
}

function ZoocasaNextApp ({
  Component,
  pageProps,
  listingParamsData,
  schoolParamsData,
  screen,
  user,
  storedUserLocation,
  storedSiteLocation,
  hasShownSitePickerScreen,
  storedConnRequestDisclaimers,
  features,
  tenantId,
  isCrawler,
  isCrawlerLighthouse,
  isWebView,
  functionalCookiesEnabled,
  advertisingCookiesEnabled,
  lastSearchLocationCookie,
  storedUserPopularCities,
  deviceId,
  isMobile,
  isBlogWidget,
}: ZoocasaNextProps
) {
  const [queryClient] = React.useState(
    () =>
      new QueryClient({
        defaultOptions: {
          queries: {
            // With SSR, we usually want to set some default staleTime
            // above 0 to avoid refetching immediately on the client
            staleTime: 60 * 1000, // 1 minute
          },
        },
      })
  );

  //  Create a global middleware to automatically append the Tenant information into fetch requests to go-search
  const tenant = getTenantById(tenantId);
  const tenantHeaderMiddleware = createTenantHeaderMiddleware(tenant.theme.id);
  MiddlewareManager.setGlobalMiddleware(tenantHeaderMiddleware);
  const router = useRouter();
  const isZoocasaHomepage = (router.pathname === '/' || router.pathname === '/us') && isZoocasaTenant(tenantId);
  const isHomePage = router.pathname === '/' || router.pathname === '/us';
  // Show default HeadData for pages that do not return a routeName prop. 
  // See the HeadData component for default values.
  // Exception: homepages do not return a routeName prop, but Zoocasa uses custom head data for the US and CA homepages.
  const headDataIsActive = !(isZoocasaHomepage || pageProps && 'routeName' in pageProps);
  const pageName = router.pathname.replace(/\//g, '-').substring(1) || 'home';
  const featuresWithOverrides = { ...configJSON.features, ...features };
  // TODO: remove this when we go live with expCommercial
  if (isTenantExpCommercial(tenantId) && router.pathname !== '/experts') {
    featuresWithOverrides.useNewExpCommercialFooter = true;
    features.useNewExpCommercialFooter = true;
  }
  const gtmContainerId = tenant.googleTagManager.containerIds.defaultContainerId;
  const ModalDialog = dynamic(() => import('components/modal-dialog'), { ssr: false });
  const Snackbar = dynamic(() => import('components/snackbar'), { ssr: false });
  const [showAccessibilityWidget, setShowAccessibilityWidget] = useState(shouldShowAccessibilityWidget(tenant.id));
  const isDesktop = useIsDesktop();
  const isBot = isCrawler || isCrawlerLighthouse;
  const isTrustArcEnabled = tenant.features.TrustArc?.enabled;
  const includeTrustarcScript = !isBot && !isBlogWidget && isTrustArcEnabled && !isEndToEndTest;
  let trustarcScriptSrc: string = '';
  if (includeTrustarcScript) {
    trustarcScriptSrc = `https://consent.trustarc.com/notice?domain=${tenant.features.TrustArc.domain}&c=${TRUSTARC_DIV_ID}&js=nj&noticeType=bb&text=true&gtm=1`;
  }

  const showLocationPickerScreen = (storedUserLocation?.countryCode === CountryCodeList.UNITED_STATES && !hasShownSitePickerScreen);

  useEffect(() => {
    const hideAccessibilityWidget = storage.get('accessibilityWidgetClosed');
    // Hide only if value is true
    if (hideAccessibilityWidget) setShowAccessibilityWidget(false);
  }, []);

  useEffect(() => {
    if (!functionalCookiesEnabled) {
      Cookies.remove(USER_LOCATION_COOKIE_NAME);
      return;
    }
    const timer = setTimeout(() => {
      if (!isEndToEndTest) {
        trackAppLoadedEvent();
      }
    }, 2000);
    return () => clearTimeout(timer);
  }, [gtmContainerId, functionalCookiesEnabled]);

  // Set New Relic app version at the earliest possible time
  if (isClientSide() && window.newrelic && window.newrelic.setApplicationVersion && !isEndToEndTest) {
    if (isProduction || isStaging || isStageTwo) {
      window.newrelic.setApplicationVersion(configJSON.version);
    } else {
      window.newrelic.setApplicationVersion(`${configJSON.version}.${configJSON.environment}`);
    }
  }

  const { locale } = useRouter();
  
  // Use LocaleManager to determine the correct locale (respects cookie preference)
  const localeManager = LocaleManager.forClient(tenantId, locale);
  const actualLocale = localeManager.getCurrentLocale();

  if (i18n && i18n.language !== actualLocale) {
    i18n.changeLanguage(actualLocale).catch(console.error);
  }

  return (
    <>
      {!isBlogWidget && functionalCookiesEnabled && !isEndToEndTest &&
        <>
          <GtmScript id={GTM_SCRIPT} strategy="afterInteractive" containerId={gtmContainerId} functionalCookiesEnabled={functionalCookiesEnabled} />  
          <ZoocasaScript id={ZOOCASA_MAKE_IT_RAIN_GTM_SCRIPT_SRC} strategy="afterInteractive" functionalCookiesEnabled={functionalCookiesEnabled} tenantId={tenantId} src="https://www.googletagmanager.com/gtag/js?id=AW-955101673" />
          <ZoocasaScript id={ZOOCASA_MAKE_IT_RAIN_GTM_SCRIPT_CONFIG} strategy="afterInteractive" functionalCookiesEnabled={functionalCookiesEnabled} tenantId={tenantId} dangerouslySetInnerHTML={{
            __html: `
              window.dataLayer = window.dataLayer || [];
              function gtag(){dataLayer.push(arguments);}
              gtag('js', new Date());
              gtag('config', 'AW-955101673');
            `,
          }} />
          <ExpScript id={HUBSPOT_TRACKING_SCRIPT} strategy="afterInteractive" functionalCookiesEnabled={functionalCookiesEnabled} tenantId={tenantId} src="//js.hs-scripts.com/24210079.js" />
        </>
      }
      {includeTrustarcScript &&
        <div id={TRUSTARC_DIV_ID}>
          <Script strategy="lazyOnload" src={trustarcScriptSrc} crossOrigin="anonymous"></Script>
        </div>
      }
      {/* Start ActiveCampaign */}
      {functionalCookiesEnabled && !isEndToEndTest && 
        <Script
          strategy="lazyOnload"
          id={ACTIVECAMPAIGN_SCRIPT}
          dangerouslySetInnerHTML={{
            __html: `(function(e,t,o,n,p,r,i){e.visitorGlobalObjectAlias=n;e[e.visitorGlobalObjectAlias]=e[e.visitorGlobalObjectAlias]||function(){(e[e.visitorGlobalObjectAlias].q=e[e.visitorGlobalObjectAlias].q||[]).push(arguments)};e[e.visitorGlobalObjectAlias].l=(new Date).getTime();r=t.createElement("script");r.src=o;r.async=true;i=t.getElementsByTagName("script")[0];i.parentNode.insertBefore(r,i)})(window,document,"https://diffuser-cdn.app-us1.com/diffuser/diffuser.js","vgo");
            vgo('setAccount', '225691203');
            vgo('setTrackByDefault', true);
            vgo('process');`,
          }}
        />  
      }
      {/* End ActiveCampaign */}
      <QueryClientProvider client={queryClient}>
        <HydrationBoundary state={pageProps?.dehydratedState}>
          <FeaturesContextProvider features={features} isWebView={isWebView} isMobile={isMobile}>
            <ThemeContextProvider tenantId={tenantId}>
              {headDataIsActive && <HeadData />}
              <ChatContextProvider>
                <ModalContextProvider isCrawler={isCrawler} tenantId={tenantId}>
                  <SnackbarContextProvider>
                    <UserContextProvider user={user} userLocation={storedUserLocation} storedSiteLocation={storedSiteLocation} storedConnRequestDisclaimers={storedConnRequestDisclaimers} storedUserPopularCities={storedUserPopularCities} isCrawler={isCrawler} functionalCookiesEnabled={functionalCookiesEnabled}>
                      <PreferencesContextProvider tenantId={tenantId} listingParamsData={listingParamsData} schoolParamsData={schoolParamsData} screen={screen} functionalCookiesEnabled={functionalCookiesEnabled} advertisingCookiesEnabled={advertisingCookiesEnabled} lastSearchLocationCookie={lastSearchLocationCookie}>
                        <SavedListingContextProvider>
                          <GrowthBookProvider user={user} deviceId={deviceId}>
                            <MUIThemeProvider theme={createTheme()}>
                              <PageBanner 
                                tenantId={tenantId}
                                featureFlag={MAINTENANCE_BANNER_FLAG}
                                defaultConfig={{
                                  title: '',
                                  message: '',
                                  buttonText: '',
                                  isVisible: false,
                                  showIcon: true,
                                  showCloseButton: true,
                                  severity: 'info',
                                  icon: <MaintenanceIcon />,
                                }}
                                storageKey={MAINTENANCE_BANNER_DISMISSED_COOKIE_NAME}
                                dismissalDuration={DISMISSAL_DURATIONS.ONE_DAY}
                                testId="maintenance-banner"
                              />
                              {!isHomePage && (
                                <PageBanner
                                  tenantId={tenantId}
                                  featureFlag={FEEDBACK_BANNER_FLAG}
                                  defaultConfig={{
                                    title: '',
                                    message: '',
                                    buttonText: '',
                                    isVisible: false,
                                    showIcon: true,
                                    showCloseButton: true,
                                    severity: 'info',
                                    icon: <NoteIcon />,
                                    gtmButtonClickEventName: GTM_FEEDBACK_BANNER_SURVEY_CLICK,
                                    gtmCloseEventName: GTM_FEEDBACK_BANNER_CLOSE,
                                    surveyUrl: FEEDBACK_BANNER_SURVEY_URL,
                                  }}
                                  storageKey={FEEDBACK_BANNER_DISMISSED_COOKIE_NAME}
                                  storageStrategy="localStorage"
                                  testId="feedback-banner"
                                />
                              )}
                              <div className={buildClassName(`body-container ${pageName}`, !isZoocasaTenant(tenantId) && 'exp-theme' )}>
                                {showAccessibilityWidget && isDesktop && (
                                  <div className='accessibility-widget-wrapper'>
                                    <AccessibilityWidget />
                                  </div>
                                )}
                                <Component {...pageProps} />
                                {ModalDialog && <ModalDialog />}
                                {!isCrawler && !isCrawlerLighthouse && showLocationPickerScreen && featuresWithOverrides.useUsListings && <LocationPickerScreen />}
                                {isMobile && Snackbar && <Snackbar />}
                              </div>
                            </MUIThemeProvider>
                          </GrowthBookProvider>
                        </SavedListingContextProvider>
                      </PreferencesContextProvider>
                    </UserContextProvider>
                  </SnackbarContextProvider>
                </ModalContextProvider>
              </ChatContextProvider>
            </ThemeContextProvider>
          </FeaturesContextProvider>
        </HydrationBoundary>
      </QueryClientProvider>
    </>
  );
}

export function reportWebVitals(metric: NextWebVitalsMetric) {
  const messageMappings: Record<string, string> = {
    FCP: 'FCP: First Contentful Pain:',
    LCP: 'LCP: Largest Contentful Paint:',
    FID: 'FID: First Input Delay:',
    TTFB: 'TTFB: Time To First Byte:',
    CLS: 'CLS: Cumulative Layout Shift:',
    'Next.js-hydration': 'Next.js Hydration:',
    'Next.js-route-change-to-render': 'Next.js Route Change to Render:',
    'Next.js-render': 'Next.js Render:',
  };

  if (!isEndToEndTest) {
    sendToGA(metric);
  }

  const metricMessage: string | undefined = messageMappings[metric.name];

  if (metricMessage) {
    console.log(metricMessage, metric.value);
  } else {
    console.log('Unrecognized Metric:', metric);
  }
}

// Import the config directly to prevent serialization issues
import config from '../../next-i18next.config.js';
export default appWithTranslation(ZoocasaNextApp, config);